'use client';

import { Search } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useFandoms } from '@/hooks/use-fandoms';
import { useTags } from '@/hooks/use-tags';
import { useUniverses } from '@/hooks/use-universes';
import { useCharacters } from '@/hooks/use-characters';
import { cn } from '@/lib/utils';
import type {
  FilterCharacter,
  FilterFandom,
  FilterRatings,
  FilterStatuses,
  FilterTag,
  FilterUniverse,
} from '@/types/filter-data';
import type {
  FilterItem,
  FilterSection,
  Tag,
} from '@/features/fanfiction-filters/types/fanfiction-filters.types';
import { mergeSelectedCharacters } from '@/features/fanfiction-filters/utils/mergeSelectedCharacters.util';
import {
  mapCharacterToFilterItem,
  mapFandomToFilterItem,
  mapTagToFilterItem,
} from '@/features/fanfiction-filters/utils/map-filter-item.util';
import { FilterPermanentLink } from '@/features/fanfiction-filters/components/FilterPermanentLink';
import { CharacterFilterList } from '@/features/fanfiction-filters/components/CharacterFilterList';
import { CheckboxItem } from '@/components/CheckboxItem';
import { FilterHeader } from '@/components/FilterHeader';
import { FilterFacetSkeleton } from '@/features/fanfiction-filters/components/FilterFacetSkeleton';
import { FILTER_LIST_LIMIT } from '@/features/fanfiction-filters/consts/FILTER_CONSTANTS';
import type { CatalogFilterFacet } from '@/features/seo-listing/types';
import { toggleSelectedFilterId } from '@/features/fanfiction-filters/utils/toggle-selected-filter-id';

const STATUS_MAPPING: Record<string, string> = {
  Завершено: 'completed',
  'В процессе': 'in-progress',
  Заморожено: 'freezed',
};

const RATING_MAPPING: Record<string, string> = {
  '0+': '0',
  '6+': '6',
  '12+': '12',
  '16+': '16',
  '18+': '18',
};

const isFacetLoading = (
  isOptionsLoading: boolean,
  loadingFacets: CatalogFilterFacet[] | undefined,
  facet: CatalogFilterFacet,
): boolean => {
  if (!isOptionsLoading) return false;
  if (!loadingFacets || loadingFacets.length === 0) return true;
  return loadingFacets.includes(facet);
};

const SearchInput = ({
  placeholder,
  value,
  onChange,
}: {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}) => (
  <div className="p-1.5 bg-white rounded-md flex items-center gap-1">
    <Search className="w-4 h-4 text-[#C0C3D3]" />
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="flex-1 text-xs text-black bg-transparent border-none outline-none placeholder:[#C0C3D3]"
    />
  </div>
);

interface FanfictionFiltersProps {
  isOriginals?: boolean;
  useNewBackend?: boolean;
  selectedFandoms?: string[];
  selectedTags?: string[];
  selectedUniverses?: string[];
  selectedStatuses?: string[];
  selectedRatings?: string[];
  selectedCharacters?: string[];
  selectedDirections?: string[];
  onFandomsChange?: (fandoms: string[]) => void;
  onTagsChange?: (tags: string[]) => void;
  onUniversesChange?: (universes: string[]) => void;
  onStatusesChange?: (statuses: string[]) => void;
  onRatingsChange?: (ratings: string[]) => void;
  /** Second arg: display names for currently selected character ids (from the filter list). */
  onCharactersChange?: (characters: string[], selectedLabels?: Record<string, string>) => void;
  /** One character from an empty fandom selection: include its fandom so SEO can open the permalink. */
  onSingleCharacterNavigate?: (characterId: string, fandomId: string) => void;
  onDirectionsChange?: (directions: string[]) => void;
  initialFandoms?: FilterFandom[];
  initialStatuses?: FilterStatuses;
  initialRatings?: FilterRatings;
  initialTags?: FilterTag[];
  initialUniverses?: FilterUniverse[];
  initialCharacters?: FilterCharacter[];
  isOptionsLoading?: boolean;
  loadingFacets?: CatalogFilterFacet[];
}

export function FanfictionFilters({
  isOriginals = false,
  useNewBackend = false,
  selectedFandoms = [],
  selectedTags = [],
  selectedUniverses = [],
  selectedStatuses = [],
  selectedRatings = [],
  selectedCharacters = [],
  onFandomsChange,
  onTagsChange,
  onUniversesChange,
  onStatusesChange,
  onRatingsChange,
  onCharactersChange,
  onSingleCharacterNavigate,
  onDirectionsChange,
  initialFandoms = [],
  initialStatuses,
  initialRatings,
  initialTags = [],
  initialUniverses = [],
  initialCharacters = [],
  isOptionsLoading = false,
  loadingFacets,
}: FanfictionFiltersProps) {
  const filterType = isOriginals ? 'original' : 'fanfic';

  // API хуки (только для поиска)
  const legacyFandomsApi = useFandoms();
  const catalogFandomsApi = useFandoms({
    useNewApi: useNewBackend,
    type: filterType,
  });
  const legacyTagsApi = useTags();
  const catalogTagsApi = useTags({
    useNewApi: useNewBackend,
    type: filterType,
  });
  const legacyUniversesApi = useUniverses();
  const catalogUniversesApi = useUniverses({
    useNewApi: useNewBackend,
    type: filterType,
  });
  const legacyCharactersApi = useCharacters();
  const catalogCharactersApi = useCharacters({
    useNewApi: useNewBackend,
  });

  const { searchFandoms, loading: fandomsLoading } = useNewBackend
    ? catalogFandomsApi
    : legacyFandomsApi;
  const { searchTags, loading: tagsLoading } = useNewBackend ? catalogTagsApi : legacyTagsApi;
  const { searchUniverses } = useNewBackend ? catalogUniversesApi : legacyUniversesApi;
  const { searchCharacters } = useNewBackend ? catalogCharactersApi : legacyCharactersApi;

  // Refs для функций поиска
  const searchFandomsRef = useRef(searchFandoms);
  const searchTagsRef = useRef(searchTags);
  const searchUniversesRef = useRef(searchUniverses);
  const searchCharactersRef = useRef(searchCharacters);

  // Обновляем refs при изменении функций
  useEffect(() => {
    searchFandomsRef.current = searchFandoms;
  }, [searchFandoms]);

  useEffect(() => {
    searchTagsRef.current = searchTags;
  }, [searchTags]);

  useEffect(() => {
    searchUniversesRef.current = searchUniverses;
  }, [searchUniverses]);

  useEffect(() => {
    searchCharactersRef.current = searchCharacters;
  }, [searchCharacters]);

  const [sections, setSections] = useState<Record<string, FilterSection>>({
    fandom: { title: 'Фэндом', isExpanded: true, hasSearch: true },
    character: { title: 'Персонажи', isExpanded: true, hasSearch: true },
    universe: { title: 'Вселенная', isExpanded: false, hasSearch: true },
    direction: { title: 'Направленность', isExpanded: true },
    genres: { title: 'Жанры', count: 123, isExpanded: true, hasSearch: true },
    status: { title: 'Статус', count: 4, isExpanded: true },
    rating: { title: 'Рейтинг', count: 5, isExpanded: true },
    tags: { title: 'Метки', count: 256, isExpanded: true, hasSearch: true },
  });

  // Инициализация state из SSR данных
  const [fandoms, setFandoms] = useState<FilterItem[]>(() =>
    initialFandoms.map((fandom) =>
      mapFandomToFilterItem(fandom, selectedFandoms.includes(fandom.id)),
    ),
  );
  const [fandomIdToName, setFandomIdToName] = useState<Record<string, string>>(() =>
    Object.fromEntries(initialFandoms.map((f) => [f.id, f.name])),
  );

  const [characters, setCharacters] = useState<FilterItem[]>(() =>
    initialCharacters.map((character) =>
      mapCharacterToFilterItem(character, selectedCharacters.includes(character.id)),
    ),
  );

  const [characterIdToName, setCharacterIdToName] = useState<Record<string, string>>(() =>
    Object.fromEntries(initialCharacters.map((c) => [c.id, c.name])),
  );
  const [, setUniverses] = useState<FilterItem[]>(() => {
    return initialUniverses.map((universe) => ({
      name: universe.name,
      count: universe.count,
      checked: selectedUniverses.includes(universe.name),
    }));
  });

  const [statuses, setStatuses] = useState<FilterItem[]>(() => {
    if (initialStatuses) {
      return [
        {
          name: 'Завершено',
          count: initialStatuses.completed,
          checked: selectedStatuses.includes('completed'),
        },
        {
          name: 'В процессе',
          count: initialStatuses['in-progress'],
          checked: selectedStatuses.includes('in-progress'),
        },
        {
          name: 'Заморожено',
          count: initialStatuses['freezed'],
          checked: selectedStatuses.includes('freezed'),
        },
      ];
    }
    return [
      {
        name: 'Завершено',
        count: 0,
        checked: selectedStatuses.includes('completed'),
      },
      {
        name: 'В процессе',
        count: 0,
        checked: selectedStatuses.includes('in-progress'),
      },
      {
        name: 'Заморожено',
        count: 0,
        checked: selectedStatuses.includes('freezed'),
      },
    ];
  });

  const [ratings, setRatings] = useState<FilterItem[]>(() => {
    if (initialRatings) {
      return [
        {
          name: '0+',
          count: initialRatings['0'],
          checked: selectedRatings.includes('0'),
        },
        {
          name: '6+',
          count: initialRatings['6'],
          checked: selectedRatings.includes('6'),
        },
        {
          name: '12+',
          count: initialRatings['12'],
          checked: selectedRatings.includes('12'),
        },
        {
          name: '16+',
          count: initialRatings['16'],
          checked: selectedRatings.includes('16'),
        },
        {
          name: '18+',
          count: initialRatings['18'],
          checked: selectedRatings.includes('18'),
        },
      ];
    }
    return [
      { name: '0+', count: 0, checked: selectedRatings.includes('0') },
      { name: '6+', count: 0, checked: selectedRatings.includes('6') },
      { name: '12+', count: 0, checked: selectedRatings.includes('12') },
      { name: '16+', count: 0, checked: selectedRatings.includes('16') },
      { name: '18+', count: 0, checked: selectedRatings.includes('18') },
    ];
  });

  const [availableTags, setAvailableTags] = useState<FilterItem[]>(() =>
    initialTags.map((tag) => mapTagToFilterItem(tag, selectedTags.includes(tag.id))),
  );
  const [, setTagIdToName] = useState<Record<string, string>>(() =>
    Object.fromEntries(initialTags.map((tag) => [tag.id, tag.name])),
  );
  const [activeTags, setActiveTags] = useState<Tag[]>(() => {
    return initialTags
      .filter((tag) => selectedTags.includes(tag.id))
      .map((tag) => ({
        id: tag.id,
        name: tag.name,
        badge:
          tag.name.toLowerCase().includes('18+') || tag.name.toLowerCase().includes('adult')
            ? '18+'
            : undefined,
        active: true,
        permanentUrl: tag.permanentUrl,
      }));
  });
  const [inactiveTags, setInactiveTags] = useState<Tag[]>(() => {
    return initialTags
      .filter((tag) => !selectedTags.includes(tag.id))
      .map((tag) => ({
        id: tag.id,
        name: tag.name,
        badge:
          tag.name.toLowerCase().includes('18+') || tag.name.toLowerCase().includes('adult')
            ? '18+'
            : undefined,
        active: false,
        permanentUrl: tag.permanentUrl,
      }));
  });

  const [fandomSearch, setFandomSearch] = useState('');
  const [universeSearch] = useState('');
  const [tagsSearch, setTagsSearch] = useState('');
  const [fandomSearchLoading, setFandomSearchLoading] = useState(false);

  // Refs for tracking selected items without retriggering search effects
  const selectedFandomsRef = useRef(selectedFandoms);
  const fandomIdToNameRef = useRef(fandomIdToName);

  useEffect(() => {
    selectedFandomsRef.current = selectedFandoms;
  }, [selectedFandoms]);

  useEffect(() => {
    fandomIdToNameRef.current = fandomIdToName;
  }, [fandomIdToName]);

  // Character label/data refs must be declared and synced before effects that read them
  const characterIdToNameRef = useRef(characterIdToName);
  const characterDataCacheRef = useRef<Record<string, FilterItem>>(
    Object.fromEntries(
      initialCharacters.map((character) => [
        character.id,
        mapCharacterToFilterItem(character, false),
      ]),
    ),
  );

  useEffect(() => {
    characterIdToNameRef.current = characterIdToName;
  }, [characterIdToName]);

  // Ref для хранения исходного списка фэндомов
  const initialFandomsRef = useRef<FilterItem[]>(
    initialFandoms.map((fandom) =>
      mapFandomToFilterItem(fandom, selectedFandoms.includes(fandom.id)),
    ),
  );

  // Обновляем ref при изменении initialFandoms
  useEffect(() => {
    initialFandomsRef.current = initialFandoms.map((fandom) =>
      mapFandomToFilterItem(fandom, selectedFandoms.includes(fandom.id)),
    );
  }, [initialFandoms, selectedFandoms]);

  // Синхронизация state с пропсами при изменении selectedFandoms
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync checked flags from parent selection props
    setFandoms((prev) =>
      prev.map((fandom) => ({
        ...fandom,
        checked: selectedFandoms.includes(fandom.id || ''),
      })),
    );
  }, [selectedFandoms]);

  // Синхронизация state с пропсами при изменении selectedCharacters
  useEffect(() => {
    setCharacters((prev) => {
      const updated = prev.map((character) => ({
        ...character,
        checked: selectedCharacters.includes(character.id || ''),
      }));

      return mergeSelectedCharacters(
        updated,
        selectedCharacters,
        characterIdToNameRef.current,
        characterDataCacheRef.current,
      );
    });
  }, [selectedCharacters]);

  // Синхронизация state с пропсами при изменении selectedTags
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync tag lists from parent selection props
    setAvailableTags((prev) => {
      const updated = prev.map((tag) => ({
        ...tag,
        checked: selectedTags.includes(tag.id || ''),
      }));

      // Вычисляем active и inactive на основе обновленных тегов
      const active = updated
        .filter((tag) => selectedTags.includes(tag.id || ''))
        .map((tag) => ({
          id: (tag.id as string) || tag.name,
          name: tag.name,
          badge:
            tag.name.toLowerCase().includes('18+') || tag.name.toLowerCase().includes('adult')
              ? '18+'
              : undefined,
          active: true,
          permanentUrl: tag.permanentUrl,
        }));
      const inactive = updated
        .filter((tag) => !selectedTags.includes(tag.id || ''))
        .map((tag) => ({
          id: (tag.id as string) || tag.name,
          name: tag.name,
          badge:
            tag.name.toLowerCase().includes('18+') || tag.name.toLowerCase().includes('adult')
              ? '18+'
              : undefined,
          active: false,
          permanentUrl: tag.permanentUrl,
        }));

      // Обновляем active и inactive теги
      setActiveTags(active);
      setInactiveTags(inactive);

      return updated;
    });
  }, [selectedTags]);

  // Синхронизация state с пропсами при изменении selectedUniverses
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync checked flags from parent selection props
    setUniverses((prev) =>
      prev.map((universe) => ({
        ...universe,
        checked: selectedUniverses.includes(universe.name),
      })),
    );
  }, [selectedUniverses]);

  // Синхронизация state с пропсами при изменении selectedStatuses
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync checked flags from parent selection props
    setStatuses((prev) =>
      prev.map((status) => ({
        ...status,
        checked: selectedStatuses.includes(STATUS_MAPPING[status.name] || status.name),
      })),
    );
  }, [selectedStatuses]);

  // Синхронизация state с пропсами при изменении selectedRatings
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync checked flags from parent selection props
    setRatings((prev) =>
      prev.map((rating) => ({
        ...rating,
        checked: selectedRatings.includes(RATING_MAPPING[rating.name] || rating.name),
      })),
    );
  }, [selectedRatings]);

  const [showMore, setShowMore] = useState<Record<string, boolean>>({
    genres: false,
    tags: false,
    universes: false,
    fandoms: false,
    characters: false,
  });

  // Refs for tracking selected characters and pass-through filter selections
  const selectedCharactersRef = useRef(selectedCharacters);
  const selectedTagsRef = useRef(selectedTags);
  const selectedUniversesRef = useRef(selectedUniverses);
  const selectedStatusesRef = useRef(selectedStatuses);
  const selectedRatingsRef = useRef(selectedRatings);

  useEffect(() => {
    selectedCharactersRef.current = selectedCharacters;
  }, [selectedCharacters]);

  useEffect(() => {
    selectedTagsRef.current = selectedTags;
  }, [selectedTags]);

  useEffect(() => {
    selectedUniversesRef.current = selectedUniverses;
  }, [selectedUniverses]);

  useEffect(() => {
    selectedStatusesRef.current = selectedStatuses;
  }, [selectedStatuses]);

  useEffect(() => {
    selectedRatingsRef.current = selectedRatings;
  }, [selectedRatings]);

  useEffect(() => {
    setFandoms(
      initialFandoms.map((fandom) =>
        mapFandomToFilterItem(fandom, selectedFandomsRef.current.includes(fandom.id)),
      ),
    );
    setFandomIdToName((previous) => ({
      ...previous,
      ...Object.fromEntries(initialFandoms.map((fandom) => [fandom.id, fandom.name])),
    }));
  }, [initialFandoms]);

  useEffect(() => {
    const nextCharacters = initialCharacters.map((character) =>
      mapCharacterToFilterItem(character, selectedCharactersRef.current.includes(character.id)),
    );
    setCharacters(
      mergeSelectedCharacters(
        nextCharacters,
        selectedCharactersRef.current,
        characterIdToNameRef.current,
        characterDataCacheRef.current,
      ),
    );
    setCharacterIdToName((previous) => ({
      ...previous,
      ...Object.fromEntries(initialCharacters.map((character) => [character.id, character.name])),
    }));
  }, [initialCharacters]);

  useEffect(() => {
    setUniverses(
      initialUniverses.map((universe) => ({
        name: universe.name,
        count: universe.count,
        checked: selectedUniversesRef.current.includes(universe.name),
      })),
    );
  }, [initialUniverses]);

  useEffect(() => {
    if (!initialStatuses) return;
    const selected = selectedStatusesRef.current;
    setStatuses([
      {
        name: 'Завершено',
        count: initialStatuses.completed,
        checked: selected.includes('completed'),
      },
      {
        name: 'В процессе',
        count: initialStatuses['in-progress'],
        checked: selected.includes('in-progress'),
      },
      {
        name: 'Заморожено',
        count: initialStatuses.freezed,
        checked: selected.includes('freezed'),
      },
    ]);
  }, [initialStatuses]);

  useEffect(() => {
    if (!initialRatings) return;
    const selected = selectedRatingsRef.current;
    setRatings([
      { name: '0+', count: initialRatings['0'], checked: selected.includes('0') },
      { name: '6+', count: initialRatings['6'], checked: selected.includes('6') },
      { name: '12+', count: initialRatings['12'], checked: selected.includes('12') },
      { name: '16+', count: initialRatings['16'], checked: selected.includes('16') },
      { name: '18+', count: initialRatings['18'], checked: selected.includes('18') },
    ]);
  }, [initialRatings]);

  useEffect(() => {
    const selected = selectedTagsRef.current;
    setAvailableTags(initialTags.map((tag) => mapTagToFilterItem(tag, selected.includes(tag.id))));
    setTagIdToName((previous) => ({
      ...previous,
      ...Object.fromEntries(initialTags.map((tag) => [tag.id, tag.name])),
    }));
    setActiveTags(
      initialTags
        .filter((tag) => selected.includes(tag.id))
        .map((tag) => ({
          id: tag.id,
          name: tag.name,
          badge:
            tag.name.toLowerCase().includes('18+') || tag.name.toLowerCase().includes('adult')
              ? '18+'
              : undefined,
          active: true,
          permanentUrl: tag.permanentUrl,
        })),
    );
    setInactiveTags(
      initialTags
        .filter((tag) => !selected.includes(tag.id))
        .map((tag) => ({
          id: tag.id,
          name: tag.name,
          badge:
            tag.name.toLowerCase().includes('18+') || tag.name.toLowerCase().includes('adult')
              ? '18+'
              : undefined,
          active: false,
          permanentUrl: tag.permanentUrl,
        })),
    );
  }, [initialTags]);

  // Ref для хранения исходного списка персонажей
  const initialCharactersRef = useRef<FilterItem[]>(
    initialCharacters.map((character) =>
      mapCharacterToFilterItem(character, selectedCharacters.includes(character.id)),
    ),
  );

  // Обновляем ref при изменении initialCharacters
  useEffect(() => {
    initialCharactersRef.current = initialCharacters.map((character) =>
      mapCharacterToFilterItem(character, selectedCharacters.includes(character.id)),
    );
    // Update character data cache for initial characters
    initialCharacters.forEach((character) => {
      characterDataCacheRef.current[character.id] = mapCharacterToFilterItem(character, false);
    });
  }, [initialCharacters, selectedCharacters]);

  const [characterSearch, setCharacterSearch] = useState('');
  const [characterSearchLoading, setCharacterSearchLoading] = useState(false);

  // Поиск по API с debounce: Фэндомы (только при изменении строки поиска)
  useEffect(() => {
    // Если поиск пустой - сразу восстанавливаем исходный список без debounce
    if (!fandomSearch.trim()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- clear search loading and restore list when query is empty
      setFandomSearchLoading(false);
      const currentSelected = selectedFandomsRef.current;
      const restoredFandoms = initialFandomsRef.current.map((fandom) => ({
        ...fandom,
        checked: currentSelected.includes(fandom.id || ''),
      }));
      setFandoms(restoredFandoms);
      return;
    }

    // Устанавливаем загрузку сразу при изменении поиска
    setFandomSearchLoading(true);

    const handler = setTimeout(async () => {
      try {
        const searchFn = searchFandomsRef.current;
        if (!searchFn || typeof searchFn !== 'function') {
          console.error('searchFandoms is not available or not a function');
          setFandomSearchLoading(false);
          return;
        }

        const results = await searchFn(fandomSearch, 0);

        // Используем refs для получения актуальных значений без триггера useEffect
        const currentSelected = selectedFandomsRef.current;
        const currentIdToName = fandomIdToNameRef.current;

        const fandomsData = results.map((item) => ({
          id: item.id,
          name: item.name,
          count: item.count,
          checked: currentSelected.includes(item.id),
          permanentUrl: item.permanentUrl,
        }));

        // Сохраняем выбранные элементы (по id) в списке даже если их нет в выдаче
        const selectedSet = new Set(currentSelected);
        const missingSelected = Array.from(selectedSet)
          .filter((id) => !fandomsData.some((f) => f.id === id))
          .map((id) => ({
            id,
            name: currentIdToName[id] || id,
            count: 0,
            checked: true,
          }));

        setFandoms([...missingSelected, ...fandomsData]);
        setFandomIdToName((prev) => ({
          ...prev,
          ...Object.fromEntries(results.map((r) => [r.id, r.name])),
        }));
      } catch (e) {
        console.error('Failed to search fandoms:', e);
      } finally {
        setFandomSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [fandomSearch]);

  // Поиск по API с debounce: Вселенные
  useEffect(() => {
    const handler = setTimeout(async () => {
      try {
        if (!universeSearch.trim()) return;

        const searchFn = searchUniversesRef.current;
        if (!searchFn || typeof searchFn !== 'function') {
          console.error('searchUniverses is not available or not a function');
          return;
        }

        const results = await searchFn(universeSearch);
        const universesData = results.map((item) => ({
          name: item.name,
          count: 0,
          checked: selectedUniverses.includes(item.name),
        }));

        // Сохраняем выбранные элементы в списке даже если их нет в выдаче
        const selectedSet = new Set(selectedUniverses);
        const missingSelected = Array.from(selectedSet)
          .filter((name) => !universesData.some((u) => u.name === name))
          .map((name) => ({ name, count: 0, checked: true }));

        setUniverses([...missingSelected, ...universesData]);
      } catch (e) {
        console.error('Failed to search universes:', e);
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [universeSearch, selectedUniverses]);

  // Поиск по API с debounce: Персонажи
  useEffect(() => {
    // Если поиск пустой - сразу восстанавливаем исходный список без debounce
    if (!characterSearch.trim()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- clear search loading and restore list when query is empty
      setCharacterSearchLoading(false);
      const currentSelected = selectedCharactersRef.current;
      const restoredCharacters = initialCharactersRef.current.map((character) => ({
        ...character,
        checked: currentSelected.includes(character.id || ''),
      }));

      setCharacters(
        mergeSelectedCharacters(
          restoredCharacters,
          currentSelected,
          characterIdToNameRef.current,
          characterDataCacheRef.current,
        ),
      );
      return;
    }

    // Устанавливаем загрузку сразу при изменении поиска
    setCharacterSearchLoading(true);

    const handler = setTimeout(async () => {
      try {
        const searchFn = searchCharactersRef.current;
        if (!searchFn || typeof searchFn !== 'function') {
          console.error('searchCharacters is not available or not a function');
          setCharacterSearchLoading(false);
          return;
        }

        const results = await searchFn(characterSearch, 10);

        // Используем refs для получения актуальных значений без триггера useEffect
        const currentSelected = selectedCharactersRef.current;
        const currentIdToName = characterIdToNameRef.current;

        const charactersData = results.map((item) => ({
          id: item.id,
          name: item.name,
          count: item.count,
          fandomTitle: item.fandom_title,
          checked: currentSelected.includes(item.id),
          permanentUrl: item.permanentUrl,
          fandomId: item.fandomId,
        }));

        // Update character data cache with full data from search results BEFORE merging
        results.forEach((result) => {
          characterDataCacheRef.current[result.id] = {
            id: result.id,
            name: result.name,
            count: result.count,
            fandomTitle: result.fandom_title,
            checked: false,
            permanentUrl: result.permanentUrl,
            fandomId: result.fandomId,
          };
        });

        setCharacters(
          mergeSelectedCharacters(
            charactersData,
            currentSelected,
            currentIdToName,
            characterDataCacheRef.current,
          ),
        );
        setCharacterIdToName((prev) => ({
          ...prev,
          ...Object.fromEntries(results.map((r) => [r.id, r.name])),
        }));
      } catch (e) {
        console.error('Failed to search characters:', e);
      } finally {
        setCharacterSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [characterSearch]);

  // Поиск по API с debounce: Теги
  useEffect(() => {
    const handler = setTimeout(async () => {
      try {
        if (!tagsSearch.trim()) {
          // При пустом поиске показываем исходный список на основе availableTags
          const activeIds = new Set(activeTags.map((t) => t.id));
          const inactive = availableTags
            .filter((tag) => !activeIds.has(tag.id || ''))
            .map((tag) => ({
              id: (tag.id as string) || tag.name,
              name: tag.name,
              active: false,
              permanentUrl: tag.permanentUrl,
            }));
          setInactiveTags(inactive);
          return;
        }

        const searchFn = searchTagsRef.current;
        if (!searchFn || typeof searchFn !== 'function') {
          console.error('searchTags is not available or not a function');
          return;
        }

        const results = await searchFn(tagsSearch);
        // Результаты поиска показываем в неактивных, исключая уже активные выбранные
        const activeIds = new Set(activeTags.map((t) => t.id));
        const inactiveFromSearch: Tag[] = results
          .filter((result) => !activeIds.has(result.id))
          .map((result) => ({
            id: result.id,
            name: result.name,
            active: false,
            permanentUrl: result.permanentUrl,
          }));

        setInactiveTags(inactiveFromSearch);
        setTagIdToName((prev) => ({
          ...prev,
          ...Object.fromEntries(results.map((r) => [r.id, r.name])),
        }));
      } catch (e) {
        console.error('Failed to search tags:', e);
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [tagsSearch, activeTags, availableTags, selectedTags]);

  const toggleSection = useCallback((key: string) => {
    setSections((prev) => ({
      ...prev,
      [key]: { ...prev[key], isExpanded: !prev[key].isExpanded },
    }));
  }, []);

  const toggleCheckbox = useCallback(
    (
      type: 'fandoms' | 'universes' | 'directions' | 'genres' | 'statuses' | 'ratings',
      index: number,
    ) => {
      const setters = {
        fandoms: setFandoms,
        universes: setUniverses,
        statuses: setStatuses,
        ratings: setRatings,
      };

      const callbacks = {
        fandoms: onFandomsChange,
        universes: onUniversesChange,
        directions: onDirectionsChange,
        genres: () => {}, // Жанры пока захардкожены
        statuses: (selectedNames: string[]) => {
          // Преобразуем русские названия статусов в английские значения
          const englishStatuses = selectedNames.map((name) => STATUS_MAPPING[name] || name);
          onStatusesChange?.(englishStatuses);
        },
        ratings: (selectedNames: string[]) => {
          // Преобразуем лейблы рейтингов в числовые значения
          const numericRatings = selectedNames.map((name) => RATING_MAPPING[name] || name);
          onRatingsChange?.(numericRatings);
        },
      };

      if (type === 'directions' || type === 'genres') {
        return;
      }

      const setItems = setters[type];
      const handleItemsChange = callbacks[type];

      setItems((prev: FilterItem[]) => {
        const newItems = prev.map((item, i) =>
          i === index ? { ...item, checked: !item.checked } : item,
        );

        // Вызываем callback с выбранными значениями
        const selectedItems = newItems.filter((item) => item.checked).map((item) => item.name);
        handleItemsChange?.(selectedItems);

        return newItems;
      });
    },
    [onFandomsChange, onUniversesChange, onDirectionsChange, onStatusesChange, onRatingsChange],
  );

  const hasSelectedItems = useCallback((items: FilterItem[]) => {
    return items.some((item) => item.checked);
  }, []);

  const toggleShowMore = useCallback((key: string) => {
    setShowMore((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  const toggleTag = useCallback(
    (tagId: string) => {
      // Check if tag is in active tags
      const activeTagIndex = activeTags.findIndex((tag) => tag.id === tagId);

      if (activeTagIndex !== -1) {
        // Move from active to inactive
        const tagToMove = activeTags[activeTagIndex];
        const newActiveTags = activeTags.filter((_, index) => index !== activeTagIndex);
        const newInactiveTags = [...inactiveTags, { ...tagToMove, active: false }];

        setActiveTags(newActiveTags);
        setInactiveTags(newInactiveTags);

        // Вызываем callback с новыми выбранными тегами
        onTagsChange?.(newActiveTags.map((tag) => tag.id));
      } else {
        // Move from inactive to active
        const inactiveTagIndex = inactiveTags.findIndex((tag) => tag.id === tagId);
        if (inactiveTagIndex !== -1) {
          const tagToMove = inactiveTags[inactiveTagIndex];
          const newInactiveTags = inactiveTags.filter((_, index) => index !== inactiveTagIndex);
          const newActiveTags = [...activeTags, { ...tagToMove, active: true }];

          setInactiveTags(newInactiveTags);
          setActiveTags(newActiveTags);

          setTagIdToName((prev) => ({
            ...prev,
            [tagId]: tagToMove.name,
          }));

          // Вызываем callback с новыми выбранными тегами
          onTagsChange?.(newActiveTags.map((tag) => tag.id));
        }
      }
    },
    [activeTags, inactiveTags, onTagsChange],
  );

  const handleCharacterLabelCached = useCallback((characterId: string, characterName: string) => {
    setCharacterIdToName((previous) => ({
      ...previous,
      [characterId]: characterName,
    }));
  }, []);

  return (
    <div className="flex flex-col gap-2 text-[#2F323A]" aria-busy={isOptionsLoading}>
      {isOptionsLoading ? <span className="sr-only">Загрузка фильтров</span> : null}
      {/* Фэндом */}
      {!isOriginals && (
        <div className="px-3.5 py-3 rounded-xl flex flex-col gap-2 bg-[#E3E5ED]">
          <FilterHeader
            isActive={selectedFandoms.length > 0 && !sections.fandom.isExpanded}
            sectionKey="fandom"
            title={sections.fandom.title}
            sections={sections}
            toggleSection={toggleSection}
            hasSelectedItems={hasSelectedItems}
          />
          {sections.fandom.isExpanded && (
            <>
              <div className="h-px bg-white mb-1" />
              <SearchInput placeholder="Поиск" value={fandomSearch} onChange={setFandomSearch} />
              <div className="flex flex-col gap-2">
                {isFacetLoading(isOptionsLoading, loadingFacets, 'fandoms') ? (
                  <FilterFacetSkeleton />
                ) : fandomsLoading || fandomSearchLoading ? (
                  <div className="text-xs text-gray-500">Загрузка...</div>
                ) : (
                  (() => {
                    return (
                      <>
                        {(() => {
                          const baseList = fandoms;
                          const list = fandomSearch.trim()
                            ? baseList
                            : showMore.fandoms
                              ? baseList
                              : baseList.slice(0, FILTER_LIST_LIMIT);
                          const filtered = fandomSearch.trim()
                            ? baseList.filter((f) =>
                                f.name.toLowerCase().includes(fandomSearch.toLowerCase()),
                              )
                            : list;
                          return filtered.map((item) => {
                            const id = (item.id as string) || item.name;
                            const isChecked = selectedFandoms.includes(id);
                            const toggleFandom = () => {
                              const updated = toggleSelectedFilterId(selectedFandoms, id);
                              onFandomsChange?.(updated);
                              if (!fandomIdToName[id]) {
                                setFandomIdToName((prev) => ({
                                  ...prev,
                                  [id]: item.name,
                                }));
                              }
                            };
                            return (
                              <CheckboxItem
                                key={`fandom-${item.id || item.name}`}
                                item={{ ...item, checked: isChecked }}
                                onPermalinkNavigate={(event) => {
                                  event.preventDefault();
                                  toggleFandom();
                                }}
                                onClick={toggleFandom}
                              />
                            );
                          });
                        })()}
                      </>
                    );
                  })()
                )}
              </div>
              {!fandomSearch && fandoms.length > FILTER_LIST_LIMIT && (
                <div
                  className="p-2 bg-white rounded-lg flex justify-center items-center cursor-pointer hover:bg-gray-50 mt-1"
                  onClick={() => toggleShowMore('fandoms')}
                >
                  <span className="text-xs text-[#1B1D22]">
                    {showMore.fandoms ? 'Скрыть' : 'Показать еще'}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Персонажи */}
      {!isOriginals && (
        <div className="px-3.5 py-3 rounded-xl flex flex-col gap-2 bg-[#E3E5ED]">
          <FilterHeader
            isActive={selectedCharacters.length > 0 && !sections.character.isExpanded}
            sectionKey="character"
            title={sections.character.title}
            sections={sections}
            toggleSection={toggleSection}
            hasSelectedItems={hasSelectedItems}
          />
          {sections.character.isExpanded && (
            <>
              <div className="h-px bg-white mb-1" />
              <SearchInput
                placeholder="Поиск"
                value={characterSearch}
                onChange={setCharacterSearch}
              />
              <div className="flex flex-col gap-2">
                {isFacetLoading(isOptionsLoading, loadingFacets, 'characters') ? (
                  <FilterFacetSkeleton />
                ) : characterSearchLoading ? (
                  <div className="text-xs text-gray-500">Загрузка...</div>
                ) : (
                  <CharacterFilterList
                    characters={characters}
                    searchQuery={characterSearch}
                    isShowMore={Boolean(showMore.characters)}
                    selectedCharacters={selectedCharacters}
                    selectedFandoms={selectedFandoms}
                    characterIdToName={characterIdToName}
                    onCharactersChange={onCharactersChange}
                    onSingleCharacterNavigate={onSingleCharacterNavigate}
                    onCharacterLabelCached={handleCharacterLabelCached}
                  />
                )}
              </div>
              {!characterSearch && characters.length > FILTER_LIST_LIMIT && (
                <div
                  className="p-2 bg-white rounded-lg flex justify-center items-center cursor-pointer hover:bg-gray-50 mt-1"
                  onClick={() => toggleShowMore('characters')}
                >
                  <span className="text-xs text-[#1B1D22]">
                    {showMore.characters ? 'Скрыть' : 'Показать еще'}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Вселенная */}
      {/* {!isOriginals && (
        <div className="px-3.5 py-3 rounded-xl flex flex-col gap-2 bg-[#E3E5ED]">
          <FilterHeader
            isActive={selectedUniverses.length > 0}
            sectionKey="universe"
            title={sections.universe.title}
          />
          {sections.universe.isExpanded && (
            <>
              <div className="h-px bg-white mb-1" />
              <SearchInput
                placeholder="Поиск"
                value={universeSearch}
                onChange={setUniverseSearch}
              />
              <div className="flex flex-col gap-2">
                {isFacetLoading(isOptionsLoading, loadingFacets, 'universes') ? (
                  <FilterFacetSkeleton />
                ) : universesLoading ? (
                  <div className="text-xs text-gray-500">Загрузка...</div>
                ) : (
                  (() => {
                    const selectedSet = new Set(selectedUniverses)
                    const selectedItems = Array.from(selectedSet).map(
                      (name) => ({ name, count: 0, checked: true })
                    )
                    const otherItems = universes.filter(
                      (item) => !selectedSet.has(item.name)
                    )
                    return (
                      <>
                        {selectedItems.map((item) => (
                          <CheckboxItem
                            key={`universe-selected-${item.name}`}
                            item={item}
                            onClick={() => {
                              const exists = selectedUniverses.includes(
                                item.name
                              )
                              const updated = exists
                                ? selectedUniverses.filter(
                                    (n) => n !== item.name
                                  )
                                : [...selectedUniverses, item.name]
                              onUniversesChange?.(updated)
                              setUniverses((prev) =>
                                prev.map((u) =>
                                  u.name === item.name
                                    ? { ...u, checked: !u.checked }
                                    : u
                                )
                              )
                            }}
                          />
                        ))}
                        {(() => {
                          const list = otherItems
                          const limited = universeSearch.trim()
                            ? list
                            : showMore.universes
                            ? list
                            : list.slice(0, FILTER_LIST_LIMIT)
                          return limited.map((item) => (
                            <CheckboxItem
                              key={`universe-${item.name}`}
                              item={item}
                              onClick={() =>
                                toggleCheckbox(
                                  'universes',
                                  universes.indexOf(item)
                                )
                              }
                            />
                          ))
                        })()}
                      </>
                    )
                  })()
                )}
              </div>
              {!universeSearch &&
                universes.filter((u) => !selectedUniverses.includes(u.name))
                  .length > FILTER_LIST_LIMIT && (
                  <div
                    className="p-2 bg-[var(--light-fill-white)] rounded-lg border border-[var(--gray-200)] flex justify-center items-center cursor-pointer hover:bg-gray-50 mt-1"
                    onClick={() => toggleShowMore('universes')}
                  >
                    <span className="text-xs text-[var(--light-text-black)]">
                      {showMore.universes ? 'Скрыть' : 'Показать еще'}
                    </span>
                  </div>
                )}
            </>
          )}
        </div>
      )} */}

      {/* Направленность */}
      {/* <div className="px-3.5 py-3 rounded-xl flex flex-col gap-2 bg-[#E3E5ED]">
                <FilterHeader sectionKey="direction" title={sections.direction.title} />
                {sections.direction.isExpanded && (
                    <>
                        <div className="h-px bg-white mb-1" />
                        <div className="flex flex-col gap-2">
                            {directions.map((item, index) => (
                                <CheckboxItem
                                    key={`direction-${item.name}`}
                                    item={item}
                                    icon={item.icon}
                                    onClick={() => toggleCheckbox("directions", index)}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div> */}

      {/* Жанры */}
      {/* <div className="px-3.5 py-3 rounded-xl flex flex-col gap-2 bg-[#E3E5ED]">
                <FilterHeader sectionKey="genres" title={sections.genres.title} count={sections.genres.count} items={genres} />
                {sections.genres.isExpanded && (
                    <>
                        <div className="h-px bg-white mb-1" />
                        <SearchInput placeholder="Поиск" value={genresSearch} onChange={setGenresSearch} />
                        <div className="flex flex-col gap-2">
                            {getFilteredItems(showMore.genres ? genres : genres.slice(0, 5), genresSearch).map((item) => (
                                <CheckboxItem
                                    key={`genre-${item.name}`}
                                    item={item}
                                    onClick={() => toggleCheckbox("genres", genres.indexOf(item))}
                                />
                            ))}
                        </div>
                        {!genresSearch && genres.length > 5 && (
                            <div
                                className="p-2 bg-[var(--light-fill-white)] rounded-lg border border-[var(--gray-200)] flex justify-center items-center cursor-pointer hover:bg-gray-50"
                                onClick={() => toggleShowMore("genres")}
                            >
                                <span className="text-xs text-[var(--light-text-black)]">
                                    {showMore.genres ? "Скрыть" : "Показать еще"}
                                </span>
                            </div>
                        )}
                    </>
                )}
            </div> */}

      {/* Статус */}
      <div className="px-3.5 py-3 rounded-xl flex flex-col gap-2 bg-[#E3E5ED]">
        <FilterHeader
          isActive={selectedStatuses.length > 0 && !sections.status.isExpanded}
          sectionKey="status"
          title={sections.status.title}
          // count={sections.status.count}
          items={statuses}
          sections={sections}
          toggleSection={toggleSection}
          hasSelectedItems={hasSelectedItems}
        />
        {sections.status.isExpanded && (
          <>
            <div className="h-px bg-white mb-1" />
            <div className="flex flex-col gap-2">
              {isFacetLoading(isOptionsLoading, loadingFacets, 'statuses') ? (
                <FilterFacetSkeleton rowsCount={3} />
              ) : (
                statuses.map((item, index) => (
                  <CheckboxItem
                    key={`status-${item.name}`}
                    item={item}
                    onClick={() => toggleCheckbox('statuses', index)}
                  />
                ))
              )}
            </div>
          </>
        )}
      </div>

      {/* Рейтинг */}
      <div className="px-3.5 py-3 rounded-xl flex flex-col gap-2 bg-[#E3E5ED]">
        <FilterHeader
          isActive={selectedRatings.length > 0 && !sections.rating.isExpanded}
          sectionKey="rating"
          title={sections.rating.title}
          // count={sections.rating.count}
          items={ratings}
          sections={sections}
          toggleSection={toggleSection}
          hasSelectedItems={hasSelectedItems}
        />
        {sections.rating.isExpanded && (
          <>
            <div className="h-px bg-white mb-1" />
            <div className="flex flex-col gap-2">
              {isFacetLoading(isOptionsLoading, loadingFacets, 'ratings') ? (
                <FilterFacetSkeleton rowsCount={5} />
              ) : (
                ratings.map((item, index) => (
                  <CheckboxItem
                    key={`rating-${item.name}`}
                    item={item}
                    onClick={() => toggleCheckbox('ratings', index)}
                  />
                ))
              )}
            </div>
          </>
        )}
      </div>

      {/* Метки */}
      <div className="px-3.5 py-3 rounded-xl flex flex-col gap-2 bg-[#E3E5ED]">
        <FilterHeader
          isActive={selectedTags.length > 0 && !sections.tags.isExpanded}
          sectionKey="tags"
          title={sections.tags.title}
          // count={availableTags.length}
          activeTags={activeTags}
          sections={sections}
          toggleSection={toggleSection}
          hasSelectedItems={hasSelectedItems}
        />
        {sections.tags.isExpanded && (
          <>
            <div className="h-px bg-white mb-1" />
            <SearchInput placeholder="Поиск" value={tagsSearch} onChange={setTagsSearch} />
            {isFacetLoading(isOptionsLoading, loadingFacets, 'tags') ? (
              <FilterFacetSkeleton variant="pills" rowsCount={8} />
            ) : tagsLoading ? (
              <div className="text-xs text-gray-500">Загрузка...</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {/* {activeTags.map((tag, index) => (
                  <div
                    key={`active-tag-${tag.name}-${index}`}
                    className="h-6 px-1.5 py-1 bg-[#505CFF] rounded-lg grid grid-cols-[1fr_max-content] items-center gap-1 cursor-pointer"
                    onClick={() => toggleTag(tag.name)}
                  >
                    <span className="text-xs text-white truncate">
                      {tag.name}
                    </span>
                    {tag.badge && (
                      <div className="px-1 bg-white rounded-md p-0 m-0 my-0.5 h-5 flex items-center justify-center">
                        <span className="text-[10px] font-medium text-[var(--light-text-black)] p-0 m-0">
                          {tag.badge}
                        </span>
                      </div>
                    )}
                    <X
                      className="w-3.5 h-3.5 text-white cursor-pointer hover:text-gray-200"
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleTag(tag.name)
                      }}
                    />
                  </div>
                ))} */}
                {(() => {
                  const baseList = availableTags;
                  const list = tagsSearch.trim()
                    ? baseList
                    : showMore.tags
                      ? baseList
                      : baseList.slice(0, FILTER_LIST_LIMIT);
                  const filtered = tagsSearch.trim()
                    ? baseList.filter((t) =>
                        t.name.toLowerCase().includes(tagsSearch.toLowerCase()),
                      )
                    : list;
                  return filtered.map((tag, index) => (
                    <div
                      key={`tag-${tag.id || tag.name}-${index}`}
                      className={cn(
                        'px-1.5 py-1 rounded-lg border  flex items-center gap-1 cursor-pointer ',
                        activeTags.some((t) => t.id === (tag.id as string))
                          ? 'border-[#505CFF] bg-[#505CFF] text-white'
                          : 'border-[#C0C3D3] hover:bg-gray-100 bg-white',
                      )}
                      onClick={() => toggleTag((tag.id as string) || tag.name)}
                    >
                      <span className="text-xs text-[var(--light-text-black)]">
                        {tag.permanentUrl ? (
                          <FilterPermanentLink
                            href={tag.permanentUrl}
                            title={tag.name}
                            isSeoOnly
                            className="text-xs text-inherit"
                          >
                            {tag.name}
                          </FilterPermanentLink>
                        ) : (
                          tag.name
                        )}
                      </span>
                      {/* {tag.badge && (
                        <div className="px-1 py-0.5 bg-white rounded-md h-5 flex items-center justify-center">
                          <span className="text-xs font-medium text-[var(--light-text-black)]">
                            {tag.badge}
                          </span>
                        </div>
                      )} */}
                    </div>
                  ));
                })()}
              </div>
            )}
            {!tagsSearch && inactiveTags.length > FILTER_LIST_LIMIT && (
              <div
                className="p-2 bg-white rounded-lg flex justify-center items-center cursor-pointer hover:bg-gray-50"
                onClick={() => toggleShowMore('tags')}
              >
                <span className="text-xs text-[var(--light-text-black)]">
                  {showMore.tags ? 'Скрыть' : 'Показать еще'}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
